import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { AuthState, useIdentify } from '@hungryroot/customer';
import { useFeatureFlag } from '@hungryroot/feature-flags';
import { logExceptionOnce, logUnknownErrorResponse } from '@hungryroot/o11y';
import { StatusCode } from '@hungryroot/rest';
import { type SaladShooterAction } from '../actions';
import { getStreamId } from '../getStreamId';
import { logSaladShooterIngest, trackNoopCalled } from '../logs';

const SALAD_SHOOTER_ORIGIN = z
  .string()
  .parse(process.env.PUBLIC_SALAD_SHOOTER_ORIGIN);
export const INGESTION_ROUTE = `${SALAD_SHOOTER_ORIGIN}/ev`;

export type EvPayload = {
  id: string;
  timestamp: Date;
  origin: 'hr-web'; // constant
  stream_id: string;
  data: Array<
    Pick<SaladShooterAction, 'kind' | 'action'> & {
      href: string;
      when: Date;
      customer_id: number | null;
    }
  >;
};

export const noop = () => {
  trackNoopCalled();
};

function generateRequestId() {
  return 'request_id_' + uuidv4();
}

export function useSaladShooter() {
  const identify = useIdentify();
  const featureFlag = useFeatureFlag('web.feature-flag.salad-shooter.enabled');

  if (identify.authState === AuthState.isLoading || featureFlag !== true) {
    return {
      track: noop,
    };
  }

  const customerId =
    identify.authState === AuthState.isAuthenticated
      ? identify.customerData.id
      : null;

  return {
    track: async function track(events: SaladShooterAction[]) {
      const streamId = getStreamId();

      // This shouldn't happen unless `track` is called during SSR,
      // but just in case, we'll log this anyway.
      if (streamId === null) {
        logExceptionOnce('Unexpected `null` stream ID', {
          key: 'useSaladShooter.track.getStreamId',
        });
      }

      const payload: EvPayload = {
        id: generateRequestId(),
        timestamp: new Date(),
        origin: 'hr-web',
        stream_id: streamId as string,
        data: events.map((event) => {
          return {
            ...event,
            href: window.location.href,
            when: new Date(),
            customer_id: customerId,
          };
        }),
      };

      logSaladShooterIngest(INGESTION_ROUTE, payload);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (streamId) {
        headers['x-saladshooter-stream-id'] = streamId;
      }

      // Intentionally not awaited, we don't want to block the main thread
      fetch(INGESTION_ROUTE, {
        method: 'POST',
        body: JSON.stringify(payload),
        credentials: 'include',
        headers: new Headers(headers),
      }).then(async (response) => {
        if (response.status !== StatusCode.SuccessAccepted) {
          const responseData = await response.json();

          logUnknownErrorResponse('Unsupported data for Salad Shooter sent', {
            key: 'useSaladShooter.track.fetch.unaccepted',
            status: response.status,
            responseData,
            requestPayload: payload,
          });
        }
      });
    },
  };
}
